import React, { useImperativeHandle, useRef, useEffect, forwardRef, useState } from "react";
import { Modal } from 'bootstrap';
import uuid from 'react-uuid';
import { useSelector } from "react-redux";
import moment from "moment";
import { PeriodSelectionWithCustom } from "../../components/TapUi";

const PasscodelogSearchModal = forwardRef(({ searchHandle, listingLoading, formSearchedElems, appliedSearchData }, ref) => {

    const all_periods = useSelector(state => state.app.all_periods ? state.app.all_periods : []);

    const modalElem = useRef(null);
    const searchModal = useRef(null);
    const modalID = uuid();

    const searchInitState = {
        search_like_passcode: "",
        search_mobile: "",
        date_range: null,
        date_range_start: null,
        date_range_end: null,
    };

    const searchFormLabel = {
        search_like_passcode: "Passcode : ",
        search_mobile: "Mobile : ",
        date_range: "Date Range : ",
    };

    const [searchForm, setSearchForm] = useState({ ...searchInitState });
    const [periods, setPeriods] = useState([]);

    useEffect(() => {
        modalElem.current = document.getElementById(modalID);
        searchModal.current = new Modal(modalElem.current, { keyboard: false, backdrop: false });
        
        let periods_data = [...all_periods, { key: 'custom', display: 'Custom Date Range' }];
        setPeriods(periods_data);
        
        return () => {
            if (searchModal.current) {
                searchModal.current.dispose();
            }
        };
    }, []);

    useEffect(() => {
        if (appliedSearchData && Object.keys(appliedSearchData).length > 0) {
            setSearchForm(prev => ({
                ...prev,
                ...appliedSearchData
            }));
        } else {
            setSearchForm({ ...searchInitState });
        }
    }, [appliedSearchData]);

    useImperativeHandle(ref, () => ({
        initModalHandler: () => {
            openModalHandle();
        },
        resetModalFilters: () => {
            resetAllFilters();
        }
    }));

    const searchModalOpen = () => searchModal.current && searchModal.current.show();
    const searchModalClose = () => {
        searchModal.current && searchModal.current.hide();
    };

    const resetAllFilters = () => {
        setSearchForm({ ...searchInitState });
        // Also clear the search criteria from parent
        searchHandle([], {});
    };

    const openModalHandle = () => {
        if (appliedSearchData && Object.keys(appliedSearchData).length > 0) {
            setSearchForm({
                ...searchInitState,
                ...appliedSearchData
            });
        } else {
            setSearchForm({ ...searchInitState });
        }
        searchModalOpen();
    };

    const handlePeriodSelect = (period, startDate, endDate) => {
        setSearchForm(prev => ({
            ...prev,
            date_range: period,
            date_range_start: startDate || null,
            date_range_end: endDate || null
        }));
    };

    const handleInputChange = (e, key) => {
        setSearchForm(prev => ({
            ...prev,
            [key]: e.target.value
        }));
    };

    const searchFormSubmit = (e = null) => {
        e && e.preventDefault();
        let searchKeys = Object.keys(searchForm);
        let searchedElems = [];
        let searchParams = {};

        searchKeys.forEach((key) => {
            let label = searchFormLabel[key];
            let value = searchForm[key];

            if (value && (Array.isArray(value) ? value.length > 0 : (typeof value === 'string' ? value.toString().trim().length > 0 : value !== null))) {
                searchParams[key] = value;
                if (label) {
                    let show_val = value;

                    if (key === "search_like_passcode") {
                        show_val = value;
                    } else if (key === "search_mobile") {
                        show_val = value;
                    } else if (key === "date_range") {
                        if (value === 'custom' && searchForm.date_range_start && searchForm.date_range_end) {
                            let start_range = moment(searchForm.date_range_start).format('DD-MMM-YYYY');
                            let end_range = moment(searchForm.date_range_end).format('DD-MMM-YYYY');
                            show_val = `Custom Date Range (${start_range} - ${end_range})`;
                        } else if (value) {
                            const foundPeriod = periods.find((s) => s.key === value);
                            show_val = foundPeriod?.display_with_date || value;
                        } else {
                            show_val = null;
                        }
                    }

                    if (show_val && show_val.toString().length > 0 && show_val !== '[]') {
                        searchedElems.push(label + `<b>${show_val}</b>`);
                    }
                }
            }
        });

        searchHandle(searchedElems, searchParams);
        searchModalClose();
    };

    return (
        <>
            <div className="modal fade" id={modalID} tabIndex="-1" ref={modalElem}>
                <div className="modal-dialog modal-md">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Passcode Log Search</h5>
                            <button 
                                type="button" 
                                className="btn-close" 
                                data-bs-dismiss="modal" 
                                onClick={() => {
                                    if (appliedSearchData && Object.keys(appliedSearchData).length > 0) {
                                        setSearchForm({ ...appliedSearchData });
                                    } else {
                                        setSearchForm({ ...searchInitState });
                                    }
                                    searchModalClose();
                                }} 
                                aria-label="Close"
                            ></button>
                        </div>
                        <div className="modal-body">
                            <form onSubmit={searchFormSubmit} id="passcodeLogSearchForm">
                                <div className="row align-items-center mb-3">
                                    <label className="col-sm-3 col-form-label col-form-label-sm text-start">Passcode</label>
                                    <div className="col-sm-9">
                                        <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            placeholder="Search by passcode..."
                                            value={searchForm.search_like_passcode || ''}
                                            onChange={(e) => handleInputChange(e, 'search_like_passcode')}
                                        />
                                    </div>
                                </div>
                                <div className="row align-items-center mb-3">
                                    <label className="col-sm-3 col-form-label col-form-label-sm text-start">Mobile</label>
                                    <div className="col-sm-9">
                                        <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            placeholder="Search by mobile number..."
                                            value={searchForm.search_mobile || ''}
                                            onChange={(e) => handleInputChange(e, 'search_mobile')}
                                        />
                                    </div>
                                </div>
                                <div className="row align-items-center mb-3">
                                    <label className="col-sm-3 col-form-label col-form-label-sm text-start">Date Range</label>
                                    <div className="col-sm-9">
                                        <PeriodSelectionWithCustom
                                            periods={periods}
                                            value={searchForm.date_range}
                                            startDate={searchForm.date_range_start}
                                            endDate={searchForm.date_range_end}
                                            className="form-control-sm"
                                            containerHeight="30px"
                                            fontSize="93%"
                                            menuPlacement={"bottom"}
                                            onSelectPeriod={(period, startDate, endDate) => handlePeriodSelect(period, startDate, endDate)}
                                        />
                                    </div>
                                </div>
                            </form>
                        </div>
                        <div className="modal-footer">
                            <button 
                                type="button" 
                                className="btn btn-secondary" 
                                data-bs-dismiss="modal"
                                onClick={() => {
                                    if (appliedSearchData && Object.keys(appliedSearchData).length > 0) {
                                        setSearchForm({ ...appliedSearchData });
                                    } else {
                                        setSearchForm({ ...searchInitState });
                                    }
                                    searchModalClose();
                                }}
                            >
                                Close
                            </button>
                            <button type="submit" form="passcodeLogSearchForm" className="btn btn-primary">
                                Search
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
});

export default PasscodelogSearchModal;